import { Controller } from "@hotwired/stimulus"
import { confirmDialog } from "../turbo_confirm"
import { positionPanel, trackPosition } from "../utils/popover_panel"

// The status picker (Tasks::StatusPickerComponent): a native popover under the status badge.
// Type to filter, arrow keys to move, Enter to pick, Escape to close. Erledigt options are
// plain Turbo links to "Abschließen"; every other option is written here through
// TasksController#update_status, whose turbo-stream re-renders the header, the side panel
// and every picker of this task.
//
// In the task forms (Tasks::StatusFieldComponent) the picker has an `input` target: picking only
// fills that hidden column_id and the trigger's badge, and saving the form writes it.
export default class extends Controller {
  static targets = ["trigger", "panel", "filter", "option", "group", "empty", "input", "label"]
  static values = {
    url: String,
    closed: Boolean,
    confirmCancel: String,
    confirmClosureLoss: String,
  }

  disconnect() {
    this.stopTracking?.()
  }

  toggle() {
    this.panelTarget.togglePopover()
  }

  openWithKeyboard(event) {
    if (event.key !== "ArrowDown") return
    event.preventDefault()
    this.panelTarget.showPopover()
  }

  close() {
    this.panelTarget.hidePopover()
  }

  panelToggled(event) {
    const open = event.newState === "open"
    this.triggerTarget.setAttribute("aria-expanded", String(open))
    if (!open) {
      this.stopTracking?.()
      this.triggerTarget.focus()
      return
    }

    // In a form the panel is as wide as the field it opens from.
    if (this.hasInputTarget) this.panelTarget.style.width = `${this.triggerTarget.offsetWidth}px`
    const reposition = () => positionPanel(this.triggerTarget, this.panelTarget)
    reposition()
    this.stopTracking = trackPosition(reposition)
    this.filterTarget.value = ""
    this.filter()
    this.filterTarget.focus()
  }

  filter() {
    const query = this.filterTarget.value.trim().toLowerCase()
    this.optionTargets.forEach((option) => {
      option.hidden = query !== "" && !option.dataset.name.includes(query)
    })
    this.groupTargets.forEach((group) => {
      group.hidden = !group.querySelector("[data-status-picker-target='option']:not([hidden])")
    })
    this.emptyTarget.classList.toggle("hidden", this.visibleOptions().length > 0)
  }

  navigate(event) {
    // Escape closes only the picker, not a drawer around it (offcanvas listens on the document).
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      return this.close()
    }

    const options = this.visibleOptions()
    if (event.key === "Enter" && document.activeElement === this.filterTarget) {
      // Enter in the filter picks the first match, so "type, Enter" is enough. Without a match it
      // does nothing: in a form it would otherwise submit the form.
      event.preventDefault()
      options[0]?.click()
      return
    }
    if (options.length === 0) return
    const index = options.indexOf(document.activeElement)

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      const next = index === -1 ? (step === 1 ? 0 : options.length - 1) : index + step
      options[(next + options.length) % options.length].focus()
    }
  }

  async choose(event) {
    const option = event.currentTarget
    if (option.getAttribute("aria-disabled") === "true") return
    if (option.getAttribute("aria-selected") === "true") return this.close()

    const category = option.dataset.category
    const name = option.textContent.trim()
    this.close()

    // confirmClosureLoss is only set on a done task with Abschlussdaten: any non-done target
    // (reopening or Storniert) deletes them.
    if (this.confirmClosureLossValue && category !== "done") {
      const message = this.confirmClosureLossValue.replace("__STATUS__", () => name)
      if (!(await confirmDialog(message, { danger: true }))) return
    } else if (category === "cancelled" && !this.closedValue) {
      if (!(await confirmDialog(this.confirmCancelValue.replace("__STATUS__", () => name)))) return
    }
    if (this.hasInputTarget) this.select(option)
    else this.save(option.dataset.columnId)
  }

  select(option) {
    this.optionTargets.forEach((other) =>
      other.setAttribute("aria-selected", String(other === option)),
    )
    this.showSelection(option)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // A list swapped in after a site change (edit_task_controller.js) brings its own selection.
  optionTargetConnected(option) {
    if (this.hasInputTarget && option.getAttribute("aria-selected") === "true")
      this.showSelection(option)
  }

  showSelection(option) {
    const { columnId, label, badge, title } = option.dataset
    this.inputTarget.value = columnId
    this.labelTarget.textContent = label
    this.labelTarget.className = badge
    if (title) this.labelTarget.title = title
    else this.labelTarget.removeAttribute("title")
  }

  async save(columnId) {
    const body = new FormData()
    body.append("column_id", columnId)
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body,
    })
    Turbo.renderStreamMessage(await response.text())
  }

  visibleOptions() {
    return this.optionTargets.filter(
      (option) => !option.hidden && option.getAttribute("aria-disabled") !== "true",
    )
  }
}
